/**
 * A sheet's batch prefetch and its install (`SheetPrefetch`): which rows one
 * prefetch reads (every list aggregate's constituents and every canonical
 * source row, or the rows a sync completion named), the batch against the
 * sheet (or, for an ephemeral sheet, the owned `:calendar` gate sheet), the
 * install of what it answers behind the ingestion gate and the install pacer,
 * the per-row fallback for what the batch could not serve, the formula
 * recompute and the aggregates' coverage.
 *
 * `SheetCellLoads` decides WHEN the sheet reads (coalescing, coverage, the
 * paced batch, sync-key refetches) and calls `run`; this unit is WHAT a read
 * installs. There is one batch engine (`#flush` → `#batch`): a prefetch and
 * the rows' own loads of the same range share a gathering window and its
 * batches, and a batch still throttled after its paced retries never fans
 * out into per-row requests. The rows, the gate, the pacer, the write revisions and the
 * listener deferral stay the sheet's, handed in through three narrow roles
 * (`SheetPrefetch.Rows`, `.Install`, `.Loads`). It does not name `Sheet`, so
 * it sits below it.
 */

import { Tracing } from "@tento-core/tracing";
import type { NaiveDate } from "@tento-chrono";
import type { Option } from "../core/option.ts";
import type { InstallPacer } from "../core/install-pacer.ts";
import type { SyncKey } from "../ids/ids.ts";
import type { RowNetworkKey } from "../ids/row-network-key.ts";
import { BatchFetchThrottledError, type BatchRowCells } from "../backends/rows-backend.ts";
import { isCanonicalKey } from "../backends/canonical-backend.ts";
import type { ModelRow } from "../rows/model-row.ts";
import type { RequestFailureReporter } from "./sheet-stores.ts";
import type { SheetWideRead } from "./sheet-stores.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { RecomputeScheduler } from "./sheet-recompute-queue.ts";
import { ComputedDataSource } from "../rows/variants/computed-data-source.ts";
import { ListDataSource, markBatchFetchedRange, reportBatchFetch } from "../rows/variants/list-data-source.ts";
import { SyncKeyWatcher } from "../sync/sync-watcher.ts";
import {
  installBatchedCells,
  noteSheetReadCoverage,
  SourceReadDiscardedError,
  SourceRow,
} from "../rows/variants/source-row.ts";

/** Types that go with `SheetPrefetch`: its three host roles and a gathered fetch's outcome. */
export namespace SheetPrefetch {
  /**
   * What became of one row's gathered fetch (`gatherRowFetch`): `"alone"` —
   * no sibling asked, so the row fetches for itself as before; `"installed"`
   * — a batch answered and installed it; `"not-served"` — the batch answered
   * it with no cells (`forbidden`, `not_found`, `error`), or the batch failed
   * other than by throttling, so the row fetches for itself.
   */
  export type GatheredFetch = "alone" | "installed" | "not-served";

  /** What one batch did for one row (`SheetPrefetch.#batch`). */
  export type RowOutcome =
    | { readonly kind: "installed"; readonly entry: Extract<BatchRowCells, { status: "ok" }> }
    | { readonly kind: "not-served" }
    /** The batch request itself failed (`throttled`: still 429 after the paced retries). */
    | { readonly kind: "batch-failed"; readonly error: unknown; readonly throttled: boolean }
    /** The row's own install failed (`discarded`: its read was discarded while the sheet lives). */
    | { readonly kind: "install-failed"; readonly error: unknown; readonly discarded: boolean };

  /** The sheet's rows and standing. */
  export interface Rows {
    /** Every row the sheet holds. */
    rows(): Iterable<ModelRow>;
    /** The sheet's route key, the batch's sheet. */
    networkKey(): string;
    /** Whether the sheet is ephemeral (its batch goes through the owned `:calendar` gate sheet). */
    isEphemeral(): boolean;
    /** Whether the sheet was disposed. */
    isRetired(): boolean;
  }

  /** How an answer is installed. */
  export interface Install {
    /** Resolves once the sheet's ingestion gate lets installs through. */
    whenIngestionReleased(): Promise<void>;
    /** The install pacer's turn for this sheet; `null` when no pacer is installed (nothing to wait for). */
    admit(): Option<Promise<InstallPacer.Lease>>;
    /** Run `fn` with the sheet's per-cell listener side effects deferred to one drain. */
    withDeferredListeners<T>(fn: () => T): T;
    /** The local-write revisions an install compares against, captured when its request goes out. */
    captureWriteRevisions(): SourceRow.WriteRevisions;
    /** Let both stores prune once the install ran or bailed. */
    releaseWriteRevisions(since: SourceRow.WriteRevisions): void;
    /** Recompute the sheet's formulas over `range`. */
    recomputeFormulas(range: NaiveDate.Range): void;
    /**
     * The sheet's frame-budget port (its recompute scheduler): a batch's rows
     * install while `remainingMs()` is above zero (at least one row a turn),
     * the rest on the turn `schedule` runs next. The immediate default
     * installs every row at once.
     */
    budget(): RecomputeScheduler;
    /**
     * The computed rows that read row `id` (`SheetRecomputeQueue.readers`): a
     * turn lands every batch row one of them reads. Absent: none does (a
     * stand-in sheet without computed rows).
     */
    readers?(id: RowLocalId): Iterable<RowLocalId>;
  }

  /** The sheet's cell loads (`SheetCellLoads`): the paced batch and the sync-key watch. */
  export interface Loads {
    fetchCellsBatchPaced(
      sheetKey: string,
      rowKeys: readonly RowNetworkKey[],
      range: NaiveDate.Range,
    ): Promise<Map<RowNetworkKey, BatchRowCells>>;
    watchSyncKeys(keys: ReadonlyMap<SyncKey, ReadonlySet<RowNetworkKey>>, range: NaiveDate.Range): void;
  }
}

/**
 * A sheet-wide read in flight (`SheetPrefetch.beginSheetRead`): its range, the
 * row keys it answered and installed, and its end (`done`: installed, bailed,
 * failed, abandoned after the bounded wait, or ended by the sheet's dispose).
 */
type SheetRead = {
  readonly range: NaiveDate.Range;
  readonly covered: Set<RowNetworkKey>;
  readonly done: Promise<void>;
  /** End the wait on it now (idempotent): it leaves the reads in flight, and `done` resolves. */
  readonly end: () => void;
  /** A row waits on it: start its bounded wait (`SHEET_READ_WAIT_MS`), once. */
  readonly arm: () => void;
};

/** One gathering window: the rows asked for over one range, and who waits on each. */
type Gathering = {
  readonly range: NaiveDate.Range;
  /** Each row key's waiters. */
  readonly waiters: Map<RowNetworkKey, ((outcome: Option<SheetPrefetch.RowOutcome>) => void)[]>;
  /** Rows a prefetch named itself (they batch even when alone). */
  readonly named: Map<RowNetworkKey, SourceRow>;
};

/** A sheet's batch prefetch and install (see the module note). */
export class SheetPrefetch {
  /** The server's limit on row keys per cells batch (lona-service `BATCH_CELLS_MAX_ROWKEYS`). */
  static readonly BATCH_MAX_ROW_KEYS = 200;
  /**
   * Microtask turns a gathering waits for the rest of its bind: the rows a
   * bind loads ask from one synchronous loop, a few awaits apart at most, and
   * the sheet's prefetch joins the same window. No timer, so a lone fetch
   * pays no timer latency.
   */
  static readonly #GATHER_TURNS = 8;
  /**
   * How long rows wait on a sheet-wide read before reading for themselves:
   * the SDK's one request deadline, `SyncKeyWatcher.POLL_TIMEOUT_MS` (10 s,
   * inside the 20 s single-request budget the UX specs hold a request to).
   * A read still out then is traced abandoned and ended; an answer that
   * lands later still installs, it no longer holds anyone. The timer is a
   * `setTimeout`, so a test's virtual clock drives it.
   */
  static readonly SHEET_READ_WAIT_MS = SyncKeyWatcher.POLL_TIMEOUT_MS;
  /**
   * Rows one install turn takes once the frame budget is spent
   * (`#installPaced`): at least one, then the rest of a reader's group, up
   * to this many. A group wider than this (a calendar list of 24-48
   * calendars) lands over several turns, its reader waiting and merging once
   * after its last row. A row costs about 0.55 ms to install (sdk-kickoff 3
   * bench, 61 events a calendar), so 8 bounds a turn's installs near 4.3 ms
   * where an unbounded 48-calendar group took 33-38 ms, and still lands a
   * 6-calendar list, or two revealed 4-calendar lists, in one turn. The merge is not split: the turn that
   * lands a list's last rows also merges it (about 10 ms at 48 calendars).
   */
  static readonly ROWS_PAST_BUDGET = 8;

  readonly #rows: SheetPrefetch.Rows;
  readonly #install: SheetPrefetch.Install;
  readonly #loads: SheetPrefetch.Loads;
  /** Gathering windows still open, by range. */
  readonly #gathering = new Map<string, Gathering>();
  /**
   * The rows a batch is reading now, with its range and the row's outcome
   * (settled before any per-row fallback runs). A row fetch the batch covers
   * waits for it instead of batching the row again (`gatherRowFetch`).
   */
  readonly #inFlight = new Map<RowNetworkKey, {
    readonly range: NaiveDate.Range;
    readonly done: Promise<SheetPrefetch.RowOutcome>;
  }>();
  /** The sheet-wide reads in flight (`beginSheetRead`); a row's own load inside one waits for it. */
  readonly #sheetReads = new Set<SheetRead>();
  /**
   * Rows a batch answered whose install the frame budget carried to a later
   * turn (`#installPaced`), by row id, each batch carrying the row with its
   * own install (two batches of different ranges can carry one row at once):
   * a read of one installs them first, in the order they were carried
   * (`finishInstall`), as a read of a carried computed row recomputes it.
   */
  readonly #carriedInstalls = new Map<RowLocalId, Set<() => void>>();
  /** Turns a batch's install took past its first (`#installPaced`), for tests and diagnostics. */
  #carriedTurns = 0;

  /** The host's policy for a non-fatal request report: the constructor's, or the one `setRequestFailureReporter` installed. */
  #reportRequestFailure: RequestFailureReporter;

  constructor(rows: SheetPrefetch.Rows, install: SheetPrefetch.Install, loads: SheetPrefetch.Loads,
    reportRequestFailure: RequestFailureReporter = (_error, report) => report()) {
    this.#reportRequestFailure = reportRequestFailure;
    this.#rows = rows;
    this.#install = install;
    this.#loads = loads;
  }

  /** Install the host's policy for non-fatal request reports (the sheet's `SheetWiring.setRequestFailureReporter`). */
  setRequestFailureReporter(reporter: RequestFailureReporter): void {
    this.#reportRequestFailure = reporter;
  }

  /** Report a non-fatal request failure through the host's policy (the sheet's `SheetInternals.reportRequestFailure`). */
  reportRequestFailure(error: Error, report: () => void): void {
    this.#reportRequestFailure(error, report);
  }

  /** Install what a batch answered for `child` (`SheetCellLoads`' `installOk`): an `ok` entry through `ingest`, any other nothing. */
  installOk(
    child: SourceRow,
    range: NaiveDate.Range,
    entry: BatchRowCells,
    since: Option<SourceRow.WriteRevisions>,
    syncKeys: Map<SyncKey, Set<RowNetworkKey>>,
  ): void {
    if (entry.status === "ok") this.ingest(child, range, entry, since, syncKeys);
  }

  /**
   * One batch over the sheet's constituents for `range`: every constituent
   * (`only` null, the prefetch), or the constituents whose row key is in
   * `only` (a sync-completion refetch). It joins the bind's gathering window,
   * so the rows' own loads of the same range go out in the same batches
   * (one batch engine: `#flush`). Then the rows the batch did not serve
   * fetch for themselves, formulas recompute, and coverage is re-marked on
   * the aggregates whose constituents were all read.
   *
   * A batch still throttled after its paced retries is load: nothing falls
   * back per row (that would turn one throttled request into one per row);
   * an ephemeral sheet warns, an owned one rejects with the throttle. Any
   * other batch failure on an ephemeral sheet falls back per row (its gate
   * sheet may not resolve); on an owned sheet it rejects.
   */
  async run(
    range: NaiveDate.Range,
    only: Option<ReadonlySet<RowNetworkKey>>,
  ): Promise<void> {
    const aggregates: ListDataSource[] = [];
    const keysToRows = new Map<RowNetworkKey, SourceRow>();
    for (const row of this.#rows.rows()) {
      if (row instanceof ListDataSource) {
        aggregates.push(row);
        for (const child of row.constituentRows()) {
          keysToRows.set(child.backendRowKey(), child);
        }
        continue;
      }
      if (
        row instanceof SourceRow &&
        !(row instanceof ComputedDataSource) &&
        (isCanonicalKey(row.lookupKey) || only != null)
      ) {
        keysToRows.set(row.backendRowKey(), row);
      }
    }
    if (only != null) {
      for (const rowKey of [...keysToRows.keys()]) {
        if (!only.has(rowKey)) keysToRows.delete(rowKey);
      }
    }
    const covered = (agg: ListDataSource): boolean =>
      agg.constituentRows().every((child) => keysToRows.has(child.backendRowKey()));
    // `sheet/prefetch/plan` records what one prefetch will fetch: aggregate and
    // constituent counts (`keys: 0` returns early; cells then load per row).
    Tracing.log("sheet/prefetch/plan", {
      origin: "prefetch",
      // Aggregate instance stamps: a later `sheets/batch-fetch` breadcrumb with
      // a gen NOT in this list means the aggregate was recreated after the
      // coordinator's coverage handoff — the recreation-storm signature.
      aggGens: aggregates.map((a) => a.generation),
      aggregates: aggregates.length,
      keys: keysToRows.size,
      // A sync-completion refetch narrowed to the rows that reported a key.
      syncRefetch: only != null,
      ephemeral: this.#rows.isEphemeral(),
      // Key families of the constituents being (batched | per-row fanned-out).
      fams: [
        ...new Set(
          [...keysToRows.keys()].map((k) => k.split(":").slice(0, 3).join(":")),
        ),
      ].slice(0, 8),
    });
    if (keysToRows.size === 0) return;

    const outcomes = await this.#join(range, keysToRows);
    const fallbacks: SourceRow[] = [];
    const answers = new Map<RowNetworkKey, BatchRowCells>();
    for (const [rowKey, outcome] of outcomes) if (outcome.kind === "installed") answers.set(rowKey, outcome.entry);
    let batchFailure: Option<{ error: unknown; throttled: boolean }> = null;
    let installFailure: Option<{ error: unknown }> = null;
    for (const [rowKey, row] of keysToRows) {
      const outcome = outcomes.get(rowKey);
      if (outcome == null || outcome.kind === "not-served") fallbacks.push(row);
      else if (outcome.kind === "batch-failed") batchFailure ??= outcome;
      else if (outcome.kind === "install-failed" && !outcome.discarded) installFailure ??= outcome;
    }
    if (batchFailure != null) {
      const ephemeral = this.#rows.isEphemeral();
      const detail = {
        where: "sheet.prefetch",
        error: batchFailure.error instanceof Error ? batchFailure.error.message : String(batchFailure.error),
        keys: keysToRows.size,
      };
      if (batchFailure.throttled) {
        // Load, not a fault, and never a fan-out. No coverage is marked, so a
        // later bind reads the rows again; an owned sheet's caller hears the throttle.
        Tracing.warn("net")("batch prefetch still throttled (429); no per-row fallback", detail);
        if (!ephemeral) throw batchFailure.error;
        return;
      } else if (ephemeral) {
        // Loud but non-fatal: the gate sheet may not resolve (a brand-new
        // sheet-less user); every constituent fetches for itself.
        this.#reportRequestFailure(
          batchFailure.error instanceof Error ? batchFailure.error : new Error(String(batchFailure.error)),
          () => Tracing.err("net")("ephemeral batch prefetch failed; falling back to per-row", detail),
        );
        for (const [rowKey, row] of keysToRows) {
          if (outcomes.get(rowKey)?.kind === "batch-failed") fallbacks.push(row);
        }
      } else {
        throw batchFailure.error;
      }
    }
    if (installFailure != null) throw installFailure.error;
    if (fallbacks.length > 0) {
      await Promise.all(fallbacks.map((child) => child.ensureLoaded(range, null)));
    }
    Tracing.log("sheet/prefetch/batch", { total: keysToRows.size, fallback: fallbacks.length });
    this.#install.recomputeFormulas(range);
    for (const agg of aggregates) {
      if (covered(agg)) markBatchFetchedRange(agg, range);
      // The prefetch owns the initial batch, so it hands the constituents'
      // `syncKeys` to each aggregate for polling.
      if (this.#rows.isEphemeral()) reportBatchFetch(agg, range, answers);
      else agg.watchBatchSyncKeys(range, answers);
    }
  }

  /**
   * A sheet-wide read of `range` going out now (the owning client's bulk
   * read, which the server answers for every row of the sheet): one read per
   * (row, range) across it and the rows' own loads. A row's own load of
   * a range inside it waits for it instead of batching (`gatherRowFetch`),
   * and each row it answered and installed (`cover`) has `range` noted on
   * its fetcher, so a later load inside it issues nothing. `settle` when the
   * read's install ran, bailed or failed; a row it did not cover then loads
   * as before. Ephemeral sheets keep their own route (nothing is covered).
   */
  beginSheetRead(range: NaiveDate.Range): SheetWideRead {
    let finish: () => void = () => {};
    const done = new Promise<void>((resolve) => finish = resolve);
    let timer: Option<ReturnType<typeof setTimeout>> = null;
    const read: SheetRead = {
      range,
      covered: new Set(),
      done,
      end: () => {
        this.#sheetReads.delete(read);
        if (timer != null) clearTimeout(timer);
        timer = null;
        finish();
      },
      arm: () => {
        if (timer != null || !this.#sheetReads.has(read)) return;
        const since = Date.now();
        timer = setTimeout(() => {
          timer = null;
          if (!this.#sheetReads.has(read)) return;
          // Unanswered past the deadline: the rows waiting on it read for themselves.
          Tracing.log("sheet/prefetch/sheet-read-abandoned", {
            start: range.start.dse, end: range.end.dse, waitedMs: Date.now() - since,
          });
          read.end();
        }, SheetPrefetch.SHEET_READ_WAIT_MS);
      },
    };
    this.#sheetReads.add(read);
    let settled = false;
    return {
      cover: (row) => {
        if (settled || this.#rows.isRetired() || this.#rows.isEphemeral()) return;
        try {
          noteSheetReadCoverage(row, range);
        } catch (error) {
          // A row replaced while the read was out reads for itself.
          if (error instanceof SourceReadDiscardedError) return;
          throw error;
        }
        read.covered.add(row.backendRowKey());
      },
      settle: () => {
        if (settled) return;
        settled = true;
        read.end();
      },
    };
  }

  /**
   * Install row `id`'s answered batch entries now if the frame budget carried
   * them to a later turn (every batch carrying it, in order): a read of a row
   * never sees it before its answers install. Nothing when none is carried.
   */
  finishInstall(id: RowLocalId): void {
    const carried = this.#carriedInstalls.get(id);
    if (carried != null) for (const finish of [...carried]) finish();
  }

  /** Install rows `ids`' carried answers (`finishInstall` each), their listeners deferred to one drain. */
  finishInstalls(ids: Iterable<RowLocalId>): void {
    this.#install.withDeferredListeners(() => {
      for (const id of ids) this.finishInstall(id);
    });
  }

  /**
   * The sheet's dispose: install every carried row now (no batch waits on a
   * turn that may never come), and end every sheet-wide read still out, so
   * no row load waits on the network for a disposed sheet (each then finds
   * the sheet retired and reads nothing). A read's own `settle` later is a no-op.
   */
  retire(): void {
    for (const carried of [...this.#carriedInstalls.values()]) for (const finish of [...carried]) finish();
    for (const read of [...this.#sheetReads]) read.end();
  }

  /** The rows whose install the budget carried, waiting for a read or a later turn. */
  carriedRows(): Iterable<RowLocalId> {
    return this.#carriedInstalls.keys();
  }

  /** What the install budget carried: rows waiting now, and the turns past a batch's first it has taken. */
  carriedInstalls(): { readonly rows: number; readonly turns: number } {
    return { rows: this.#carriedInstalls.size, turns: this.#carriedTurns };
  }

  /**
   * Install `rows` (each through `installOne`) under the sheet's frame budget:
   * rows while `remainingMs()` is above zero, at least one a turn, the rest on
   * the turn the port schedules next, each turn's rows with the listeners
   * deferred to one drain. A turn the budget stops also installs the rows
   * read by a computed row that reads one it installed (`Install.readers`):
   * a calendar list merges every calendar, so paced one calendar a turn it
   * would complete a turn per calendar late (dom-perf 57); together it merges
   * once, on the turn its first calendar lands. A carried row is registered
   * so a read installs it first (`finishInstall`), alone. Past the budget a
   * turn takes at most `ROWS_PAST_BUDGET` rows; a group it splits goes first
   * on the next turn, and its reader waits for its last row (the carried rows
   * are registered before the turn's drain). Resolves once every row
   * installed.
   */
  #installPaced(
    rows: ReadonlyMap<RowNetworkKey, SourceRow>,
    installOne: (rowKey: RowNetworkKey) => void,
  ): Promise<void> {
    const budget = this.#install.budget();
    const queue = new Map<RowNetworkKey, SourceRow>(rows);
    let finished: () => void = () => {};
    const done = new Promise<void>((resolve) => finished = resolve);
    const take = (rowKey: RowNetworkKey): void => {
      const row = queue.get(rowKey);
      if (row == null) return;
      queue.delete(rowKey);
      const mine = carry.get(rowKey);
      const waiting = this.#carriedInstalls.get(row.id);
      if (mine != null && waiting != null && waiting.delete(mine) && waiting.size === 0) this.#carriedInstalls.delete(row.id);
      installOne(rowKey);
      if (queue.size === 0) finished();
    };
    const carry = new Map<RowNetworkKey, () => void>();
    /** Readers whose inputs a turn began landing: the rest of their group goes first. */
    const started = new Set<RowLocalId>();
    /** Each queued row's readers, asked once a batch (a group split over turns asks every turn). */
    const readerCache = new Map<RowLocalId, readonly RowLocalId[]>();
    const readersOf = (id: RowLocalId): readonly RowLocalId[] => {
      let readers = readerCache.get(id);
      if (readers == null) {
        readers = [...(this.#install.readers?.(id) ?? [])];
        readerCache.set(id, readers);
      }
      return readers;
    };
    const turn = (first: boolean): void => {
      if (!first) this.#carriedTurns++;
      this.#install.withDeferredListeners(() => {
        const took: RowLocalId[] = [];
        let past = 0;
        const room = (): boolean => past < SheetPrefetch.ROWS_PAST_BUDGET || budget.remainingMs() > 0;
        const takeRow = (rowKey: RowNetworkKey, row: SourceRow): void => {
          if (budget.remainingMs() <= 0) past++;
          take(rowKey);
          took.push(row.id);
        };
        // The rest of a group an earlier turn began landing goes first.
        if (started.size > 0) {
          for (const [rowKey, row] of [...queue]) {
            if (!room()) break;
            if (started.has(row.id) || readersOf(row.id).some((reader) => started.has(reader))) takeRow(rowKey, row);
          }
        }
        for (const [rowKey, row] of [...queue]) {
          if (took.length > 0 && budget.remainingMs() <= 0) break;
          takeRow(rowKey, row);
        }
        const readers = new Set(took.flatMap((id) => readersOf(id)));
        if (queue.size > 0 && readers.size > 0) {
          for (const [rowKey, row] of [...queue]) {
            if (!room()) break;
            for (const reader of readersOf(row.id)) {
              if (readers.has(reader)) {
                takeRow(rowKey, row);
                break;
              }
            }
          }
        }
        for (const reader of readers) started.add(reader);
        // Registered before this turn's drain: a reader whose group the cap
        // split waits for its last input and merges once.
        for (const [rowKey, row] of queue) {
          if (carry.has(rowKey)) continue;
          const finish = (): void => this.#install.withDeferredListeners(() => take(rowKey));
          carry.set(rowKey, finish);
          const waiting = this.#carriedInstalls.get(row.id) ?? new Set<() => void>();
          waiting.add(finish);
          this.#carriedInstalls.set(row.id, waiting);
        }
      });
      if (queue.size === 0) return;
      budget.schedule(() => turn(false));
    };
    if (queue.size === 0) return Promise.resolve();
    turn(true);
    return done;
  }

  /**
   * One row's own fetch of `range`, gathered with every other row of this
   * sheet that asks for the same range within a few microtask turns (a bind
   * loads each demanded row through its own `ensureLoaded`), and with the
   * sheet's prefetch of that range when one opens in the same window: they
   * go out as batches of at most `BATCH_MAX_ROW_KEYS` row keys, installed
   * in the prefetch's order (capture, batch, gate, admit, deferred install,
   * release, recompute), instead of one GET each. A lone fetch answers
   * `"alone"` and the row fetches for itself exactly as before. A row a
   * batch is already reading over `range` waits for that batch.
   *
   * Each caller gets its own row's outcome: `"installed"`, `"not-served"`
   * (its entry carried no cells, or its batch failed other than by
   * throttling; it fetches for itself), or a rejection: the throttle's
   * `BatchFetchThrottledError` when the batch was still throttled after its
   * paced retries (no per-row fan-out into the 429 window), or its own
   * install's failure (`SourceReadDiscardedError` when its read was
   * discarded). On an ephemeral sheet only canonical rows gather (its other
   * rows exist only on the client).
   */
  async gatherRowFetch(range: NaiveDate.Range, rowKey: RowNetworkKey): Promise<SheetPrefetch.GatheredFetch> {
    if (this.#rows.isRetired()) return "alone";
    for (const read of this.#rows.isEphemeral() ? [] : this.#sheetReads) {
      if (read.range.start.dse > range.start.dse || range.end.dse > read.range.end.dse) continue;
      read.arm();
      await read.done;
      if (read.covered.has(rowKey)) return "installed";
      break;
    }
    if (this.#rows.isRetired()) return "alone";
    const reading = this.#inFlight.get(rowKey);
    const outcome = reading != null && reading.range.start.dse <= range.start.dse && range.end.dse <= reading.range.end.dse
      ? await reading.done
      : await this.#wait(range, rowKey);
    if (outcome == null) return "alone";
    switch (outcome.kind) {
      case "installed":
        return "installed";
      case "not-served":
        return "not-served";
      case "batch-failed":
        if (outcome.throttled) throw outcome.error;
        return "not-served";
      case "install-failed":
        throw outcome.error;
    }
  }

  /** The gathering window for `range`, opened (with its flush scheduled) if none is. */
  #gatheringFor(range: NaiveDate.Range): Gathering {
    const key = `${range.start.toString()}..${range.end.toString()}`;
    const open = this.#gathering.get(key);
    if (open != null) return open;
    const opened: Gathering = { range, waiters: new Map(), named: new Map() };
    this.#gathering.set(key, opened);
    void (async () => {
      for (let turn = 0; turn < SheetPrefetch.#GATHER_TURNS; turn++) await Promise.resolve();
      if (this.#gathering.get(key) === opened) this.#gathering.delete(key);
      await this.#flush(opened);
    })();
    return opened;
  }

  /** Wait in `range`'s window for `rowKey`'s outcome (`null`: it went alone). */
  #wait(range: NaiveDate.Range, rowKey: RowNetworkKey): Promise<Option<SheetPrefetch.RowOutcome>> {
    const gathering = this.#gatheringFor(range);
    return new Promise((resolve) => {
      const list = gathering.waiters.get(rowKey) ?? [];
      list.push(resolve);
      gathering.waiters.set(rowKey, list);
    });
  }

  /** A prefetch's rows join `range`'s window; resolves with each row's outcome. */
  async #join(
    range: NaiveDate.Range,
    rows: ReadonlyMap<RowNetworkKey, SourceRow>,
  ): Promise<Map<RowNetworkKey, SheetPrefetch.RowOutcome>> {
    const gathering = this.#gatheringFor(range);
    for (const [rowKey, row] of rows) gathering.named.set(rowKey, row);
    const outcomes = new Map<RowNetworkKey, SheetPrefetch.RowOutcome>();
    await Promise.all([...rows.keys()].map(async (rowKey) => {
      const outcome = await new Promise<Option<SheetPrefetch.RowOutcome>>((resolve) => {
        const list = gathering.waiters.get(rowKey) ?? [];
        list.push(resolve);
        gathering.waiters.set(rowKey, list);
      });
      if (outcome != null) outcomes.set(rowKey, outcome);
    }));
    return outcomes;
  }

  /**
   * The one batch engine: a closed window's rows go out in batches of at most
   * `BATCH_MAX_ROW_KEYS`, and each waiter hears its row's outcome. The rows
   * are the ones a prefetch named, plus the stored source rows of this sheet
   * whose own loads asked (on an ephemeral sheet only the canonical ones).
   * Rows that asked alone, with nothing named, go alone (`null`).
   */
  async #flush(gathering: Gathering): Promise<void> {
    const { range, waiters, named } = gathering;
    const tell = (rowKey: RowNetworkKey, outcome: Option<SheetPrefetch.RowOutcome>) => {
      for (const waiter of waiters.get(rowKey) ?? []) waiter(outcome);
    };
    const rows = new Map<RowNetworkKey, SourceRow>(named);
    if (!this.#rows.isRetired()) {
      const ephemeral = this.#rows.isEphemeral();
      for (const row of this.#rows.rows()) {
        if (!(row instanceof SourceRow) || row instanceof ComputedDataSource || row instanceof ListDataSource) continue;
        if (row.cellsDerived) continue;
        if (ephemeral && !isCanonicalKey(row.lookupKey)) continue;
        const rowKey = row.backendRowKey();
        if (waiters.has(rowKey) && !rows.has(rowKey)) rows.set(rowKey, row);
      }
    }
    if (named.size === 0 && rows.size < 2) {
      for (const rowKey of waiters.keys()) tell(rowKey, null);
      return;
    }
    for (const rowKey of waiters.keys()) if (!rows.has(rowKey)) tell(rowKey, null);
    const keys = [...rows.keys()];
    const gate = this.#rows.isEphemeral() ? ":calendar" : this.#rows.networkKey();
    Tracing.log("sheet/prefetch/gathered", {
      rows: keys.length, named: named.size, start: range.start.dse, end: range.end.dse,
    });
    const chunks: RowNetworkKey[][] = [];
    for (let i = 0; i < keys.length; i += SheetPrefetch.BATCH_MAX_ROW_KEYS) {
      chunks.push(keys.slice(i, i + SheetPrefetch.BATCH_MAX_ROW_KEYS));
    }
    await Promise.all(chunks.map(async (chunk) => {
      // A prefetch in the window recomputes once, after its per-row fallbacks.
      const outcomes = await this.#batch(gate, range, chunk, rows, named.size === 0);
      for (const rowKey of chunk) tell(rowKey, outcomes.get(rowKey) ?? { kind: "not-served" });
    }));
  }

  /**
   * One batch: capture the write revisions, request (paced), wait for the
   * ingestion gate and the install pacer, install every `ok` entry with the
   * listeners deferred, release, watch the entries' sync keys, recompute the
   * formulas (`recompute`; a prefetch recomputes after its fallbacks). Each row is
   * claimed in flight until its outcome is known.
   */
  async #batch(
    gate: string,
    range: NaiveDate.Range,
    chunk: readonly RowNetworkKey[],
    rows: ReadonlyMap<RowNetworkKey, SourceRow>,
    recompute: boolean,
  ): Promise<Map<RowNetworkKey, SheetPrefetch.RowOutcome>> {
    const outcomes = new Map<RowNetworkKey, SheetPrefetch.RowOutcome>();
    const settled = new Map<RowNetworkKey, (outcome: SheetPrefetch.RowOutcome) => void>();
    for (const rowKey of chunk) {
      const done = new Promise<SheetPrefetch.RowOutcome>((resolve) => settled.set(rowKey, resolve));
      this.#inFlight.set(rowKey, { range, done });
    }
    const settle = (rowKey: RowNetworkKey, outcome: SheetPrefetch.RowOutcome) => {
      outcomes.set(rowKey, outcome);
    };
    // Joined readers must observe the complete install, including sync-key
    // registration and formula recomputation, before a row reports success.
    const publish = (rowKey: RowNetworkKey, outcome: SheetPrefetch.RowOutcome) => {
      const entry = this.#inFlight.get(rowKey);
      if (entry != null && entry.range === range) this.#inFlight.delete(rowKey);
      settled.get(rowKey)?.(outcome);
    };
    // The local-write revisions are captured when the request goes OUT, so a
    // cell written or cleared while it is in flight (the round trip, any 429
    // pause, the ingestion-gate wait) keeps its local state: the response is
    // the server's state from before that write (c9 event-flow-copy-to-calendar).
    const since = this.#install.captureWriteRevisions();
    let result: Map<RowNetworkKey, BatchRowCells>;
    try {
      result = await this.#loads.fetchCellsBatchPaced(gate, chunk, range);
    } catch (error) {
      this.#install.releaseWriteRevisions(since);
      const throttled = error instanceof BatchFetchThrottledError;
      if (!throttled) Tracing.log("sheet/prefetch/batch-failed", { rows: chunk.length, error: String(error) });
      for (const rowKey of chunk) {
        const outcome = { kind: "batch-failed", error, throttled } as const;
        settle(rowKey, outcome);
        publish(rowKey, outcome);
      }
      return outcomes;
    }
    const syncKeys = new Map<SyncKey, Set<RowNetworkKey>>();
    let pace: Option<InstallPacer.Lease> = null;
    try {
      try {
        await this.#install.whenIngestionReleased();
        const admission = this.#install.admit();
        pace = admission == null ? null : await admission;
        const answered = new Map<RowNetworkKey, SourceRow>();
        for (const rowKey of chunk) {
          const entry = result.get(rowKey);
          const row = rows.get(rowKey);
          if (entry == null || entry.status !== "ok" || row == null) settle(rowKey, { kind: "not-served" });
          else answered.set(rowKey, row);
        }
        await this.#installPaced(answered, (rowKey) => {
          const entry = result.get(rowKey);
          const row = rows.get(rowKey);
          if (entry == null || entry.status !== "ok" || row == null) return;
          // Each row installs on its own: one discarded read fails that row
          // alone, never its siblings.
          try {
            installBatchedCells(row, range, entry.cells, entry.metadata, since);
            settle(rowKey, { kind: "installed", entry });
            for (const key of entry.syncKeys ?? []) {
              const keyed = syncKeys.get(key) ?? new Set<RowNetworkKey>();
              keyed.add(rowKey);
              syncKeys.set(key, keyed);
            }
          } catch (error) {
            // A constituent replaced while the batch was out discards its
            // own read; a retired sheet's discard is the batch's failure.
            const discarded = error instanceof SourceReadDiscardedError && !this.#rows.isRetired();
            settle(rowKey, { kind: "install-failed", error, discarded });
          }
        });
      } finally {
        this.#install.releaseWriteRevisions(since);
      }
      if (syncKeys.size > 0) this.#loads.watchSyncKeys(syncKeys, range);
      if (recompute) this.#install.recomputeFormulas(range);
    } catch (error) {
      for (const rowKey of chunk) {
        const outcome = outcomes.get(rowKey);
        if (outcome == null || outcome.kind === "installed") {
          settle(rowKey, { kind: "install-failed", error, discarded: false });
        }
      }
    } finally {
      pace?.settle();
      for (const rowKey of chunk) {
        const outcome: SheetPrefetch.RowOutcome = outcomes.get(rowKey) ?? { kind: "not-served" };
        settle(rowKey, outcome);
        publish(rowKey, outcome);
      }
    }
    return outcomes;
  }

  /**
   * Install one `ok` batch entry on its row and record the entry's sync keys
   * against its row key in `syncKeys`. Every batch path (the sheet prefetch,
   * the ephemeral `:calendar` gate, the shared aggregate batch) installs
   * through here and then hands `syncKeys` to `SheetCellLoads.watchSyncKeys` once per
   * batch, so a batched row outside every aggregate (stickers, a provider
   * weather row and its logs) is polled and re-read when its sync completes
   * like an aggregate's constituent — its keys used to be dropped, and the
   * row showed nothing until a refresh.
   */
  ingest(
    child: SourceRow,
    range: NaiveDate.Range,
    entry: Extract<BatchRowCells, { status: "ok" }>,
    since: Option<SourceRow.WriteRevisions>,
    syncKeys: Map<SyncKey, Set<RowNetworkKey>>,
  ): void {
    try {
      installBatchedCells(child, range, entry.cells, entry.metadata, since);
    } catch (error) {
      // A constituent replaced while the batch was out (a list rebuilt under
      // a changed calendar config) discards its own read; the live sheet's
      // other rows still install, and the replacement reads for itself. A
      // retired sheet's discard still rejects the batch (deliberate model
      // retirement: nothing of a disposed model installs or fulfils).
      if (error instanceof SourceReadDiscardedError && !this.#rows.isRetired()) return;
      throw error;
    }
    for (const key of entry.syncKeys ?? []) {
      const rows = syncKeys.get(key) ?? new Set<RowNetworkKey>();
      rows.add(child.backendRowKey());
      syncKeys.set(key, rows);
    }
  }
}
